import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Flex, Card, DatePicker, Select, Button, Empty, Input, Alert } from "antd"
import { importClientHistory } from "store/clients"
import CustomPagination from "components/common/CustomPagination"
import DataTable from "ui/DataTable"
import IconWithBg from "ui/IconWithBg"
import { CheckCircleOutlined, CloseCircleOutlined, LeftOutlined, SearchOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import { routes } from "router"
import { Text, Title } from "ui/Typography"
import { ImportsNotFound } from "ui/Icon/DSIcon/ImportsNotFound"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import styles from "./viewImport.module.css"
import { getHeaders } from "./headers"
import StatusTag from "../ClientDetails/ClientDetailsTabs/BookingTab/BookingTable/StatusTag"
import Spinner from "components/common/Spinner"
import { debounce } from "lodash"
import { useDevice } from "hooks/useDevice"
import DataTableResponsive from "ui/DataTableResponsive"
import ImportClientList from "../ClientTable/Modals/ImportClientList"
import { DSIcon } from "ui/Icon/DSIcon"
const { RangePicker } = DatePicker
const dateFormat = "MM/DD/YYYY"
const DELAY_TIME = 300

const ViewImportHistory = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [openImportClientList, setOpenImportClientList] = useState(false)
  const { importClientHistoryData, loading } = useSelector((state) => state.clients)
  const [sortKey, setSortKey] = useState("")
  const [sortOrder, setSortOrder] = useState("")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [dateRange, setDateRange] = useState(null)
  const search = searchParams?.get("search")
  const status = searchParams?.get("status")
  const { isTablet, isMobile } = useDevice()

  const dataSource = importClientHistoryData?.data?.map((item) => {
    const { id, status, current_status, filename, started_at, result } = item || {}
    return {
      key: id,
      status: <StatusTag status={current_status} statusMessage={status} />,
      filename: result?.file?.[0] ? (
        <Text className={styles.filenameDisabled}>{filename}</Text>
      ) : (
        <Text className={styles.filenameLink} onClick={() => navigate(routes.guideImportClientDetail({ id }))}>
          {filename}
        </Text>
      ),
      started_at,
      details: (
        <Flex vertical gap={8}>
          {result?.success_count > 0 && (
            <Text className="report-success">
              <CheckCircleOutlined />{" "}
              <strong>{t("clients.import_records.clients_imported_successfully", { count: result.success_count })}</strong>
            </Text>
          )}
          {result?.failed_count > 0 && (
            <Text className="report-error">
              <CloseCircleOutlined />{" "}
              <strong>{t("clients.import_records.clients_failed_due_to_errors", { count: result.failed_count })}</strong>
            </Text>
          )}
          {current_status === "in_progress" && (
            <Text className="report-in-progress">
              <strong>{t("clients.import_records.importing_in_progress")}</strong>
            </Text>
          )}
          {current_status === "failed" && (
            <Text className="report-error">
              <strong>{result?.file?.[0] || ""}</strong>
            </Text>
          )}
        </Flex>
      )
    }
  })

  const metadata = importClientHistoryData?.metadata

  const handleImportClients = () => {
    setOpenImportClientList(true)
  }

  // Build columns and mobile label mapping
  const columns = React.useMemo(() => getHeaders({ field: sortKey, order: sortOrder }), [sortKey, sortOrder])
  const { labelMap, fieldOrder } = React.useMemo(() => {
    const map = {}
    const order = []
    for (const col of columns) {
      const key = col.dataIndex || col.key
      if (!key) continue
      order.push(key)
      map[key] = col.title
    }
    return { labelMap: map, fieldOrder: order }
  }, [columns])

  const onSortChange = (_pagination, _filters, sorter) => {
    // Ant Design Table onChange passes (pagination, filters, sorter, extra)
    if (sorter?.field) {
      setSortKey(sorter.field)
      setSortOrder(sorter.order)
      setPage(1) // Reset to first page when sorting changes
    } else {
      // Clear sorting if sorter is empty
      setSortKey("")
      setSortOrder("")
    }
  }

  const handleBackToClients = () => {
    navigate(routes.guideClientsPath())
  }

  const handleMobileRowClick = (data) => {
    navigate(routes.guideImportClientDetail({ id: data?.key }))
  }

  const buildApiParams = useCallback(() => {
    const params = { page, pageSize }

    if (search) params.search = search
    if (status) params.status = status
    if (dateRange) {
      params.start_date = dateRange.startDate
      params.end_date = dateRange.endDate
    }
    if (sortKey && sortOrder) {
      params.order_by = sortKey
      params.order_direction = sortOrder === "ascend" ? "asc" : "desc"
    }
    return params
  }, [page, pageSize, search, status, dateRange, sortKey, sortOrder])

  useEffect(() => {
    dispatch(importClientHistory(buildApiParams()))
  }, [dispatch, buildApiParams])

  const debouncedUpdate = useMemo(
    () =>
      debounce((value) => {
        const updatedParams = new URLSearchParams(searchParams.toString())
        updatedParams.set("search", value)
        setSearchParams(updatedParams)
      }, DELAY_TIME),
    [searchParams, setSearchParams]
  )

  const onChangeFilters = useCallback(
    (e) => {
      setPage(1)
      const { name, value } = e.target || {}
      const updatedParams = new URLSearchParams(searchParams.toString())

      if (name === "search") {
        debouncedUpdate(value)
      } else if (name === "status") {
        if (value) {
          updatedParams.set("status", value)
        } else {
          updatedParams.delete("status")
        }
        setSearchParams(updatedParams)
      }
    },
    [debouncedUpdate, searchParams, setSearchParams]
  )

  const handleDateChange = (_, dateStrings) => {
    const updatedParams = new URLSearchParams(searchParams.toString())

    if (dateStrings?.length === 2 && dateStrings[0] && dateStrings[1]) {
      setDateRange({
        startDate: dateStrings[0],
        endDate: dateStrings[1]
      })
      updatedParams.set("start_date", dateStrings[0])
      updatedParams.set("end_date", dateStrings[1])
    } else {
      setDateRange(null)
      updatedParams.delete("start_date")
      updatedParams.delete("end_date")
    }

    setPage(1)
    setSearchParams(updatedParams)
  }

  return (
    <div className="w-full">
      <Flex gap={16}>
        <IconWithBg icon={<LeftOutlined />} onClick={handleBackToClients} />
        <Title level={3}>{t("clients.import_history")}</Title>
      </Flex>
      <Card className="mt-3xl">
        <Flex vertical gap={16} wrap>
          <Title level={5}>{t("clients.import_history")}</Title>
          {isTablet || isMobile ? (
            <Alert
              showIcon
              className={styles.infoAlert}
              message={t("clients.laptop_only_import_list")}
              type="info"
              closable
              icon={<DSIcon name="info" />}
            />
          ) : null}

          <Flex gap={16} className="w-full">
            <Input
              prefix={<SearchOutlined aria-hidden="true" />}
              placeholder={t("global.search")}
              className={styles.searchInput}
              name="search"
              allowClear
              value={search || ""}
              onChange={onChangeFilters}
              size="large"
            />
            <RangePicker format={dateFormat} onChange={handleDateChange} placeholder={["From", "To"]} size="large" />
            <Select
              placeholder="Select Status"
              style={{ width: 200 }}
              allowClear
              value={status || undefined}
              onChange={(value) => {
                const e = {
                  target: {
                    name: "status",
                    value: value || ""
                  }
                }
                onChangeFilters(e)
              }}
              options={[
                { value: "success", label: "Success" },
                { value: "failed", label: "Failed" },
                { value: "in_progress", label: "In Progress" }
              ]}
              size="large"
            />
            {isTablet || isMobile ? null : (
              <Button
                color="primary"
                variant="solid"
                size="large"
                aria-label={t("clients.import_client_list")}
                onClick={handleImportClients}
                className="ml-auto"
              >
                {t("clients.import_client_list")}
              </Button>
            )}
          </Flex>
          <WithEmptyStateWrapper
            name={t("clients.import_history")}
            hasItems={importClientHistoryData?.data?.length > 0}
            fallbackTitle={"abc"}
            fallback={
              loading ? (
                <Spinner />
              ) : (
                <div className={styles.contentWrapper}>
                  <Empty
                    image={<ImportsNotFound />}
                    description={
                      <>
                        <Text strong size="lg">
                          {"No imports yet"}
                        </Text>
                        <p style={{ color: "#888" }}>{"Upload your first client list to get started."}</p>
                        <Button
                          color="primary"
                          variant="outlined"
                          size="large"
                          aria-label={t("clients.import_client_list")}
                          onClick={handleImportClients}
                          className="flex-1"
                        >
                          {t("clients.import_client_list")}
                        </Button>
                      </>
                    }
                  />
                </div>
              )
            }
          >
            {isTablet || isMobile ? (
              <DataTableResponsive data={dataSource} onItemClick={handleMobileRowClick} labelMap={labelMap} fieldOrder={fieldOrder} />
            ) : (
              <>
                <DataTable
                  columns={columns}
                  dataSource={dataSource}
                  pagination={false}
                  rowClickKey="key"
                  onChange={onSortChange}
                  rowSelection={false}
                  rowKey={(record) => record.key}
                />
                {metadata?.total_results > 10 && (
                  <div className="mb-m">
                    <CustomPagination
                      metadata={metadata}
                      pageSize={pageSize}
                      total={metadata?.total_results || 0}
                      onPageChange={setPage}
                      onPageSizeChange={(newSize) => {
                        setPageSize(newSize)
                        setPage(1)
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </WithEmptyStateWrapper>
        </Flex>
      </Card>
      <ImportClientList open={openImportClientList} onClose={() => setOpenImportClientList(false)} setOpenImportClientList={setOpenImportClientList} />
    </div>
  )
}

export default ViewImportHistory
